/**
 * 要員配置テーブル共通コンポーネント
 *
 * 予算目標管理と案件作成・編集画面で共通利用
 *
 * @created 2025-12-11
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Alert,
  Autocomplete,
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import AsyncAutocomplete from '../Common/AsyncAutocomplete';
import { employeeApi } from '../../services/api';
import { salesMasterApi } from '../../services/salesApi';
import type { Vendor, ResourceAllocation } from '../../services/salesApi';

interface TechEmployee {
  employee_id: string;
  name: string;
  rank?: string;
}

interface ResourceAllocationTableProps {
  resources: Partial<ResourceAllocation>[];
  onChange: (resources: Partial<ResourceAllocation>[]) => void;
  disabled?: boolean;
  fiscalYear: number;
  showAlert?: boolean;
}

/**
 * 要員配置テーブルコンポーネント
 *
 * @param resources - 要員配置データ配列
 * @param onChange - データ変更時のコールバック
 * @param disabled - 読み取り専用モード
 * @param fiscalYear - 年度（初期値設定に使用）
 * @param showAlert - アラートメッセージ表示
 */
const ResourceAllocationTable: React.FC<ResourceAllocationTableProps> = ({
  resources,
  onChange,
  disabled = false,
  fiscalYear,
  showAlert = true,
}) => {
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [rankCosts, setRankCosts] = useState<Record<string, number>>({});
  const [focusedField, setFocusedField] = useState<string | null>(null);

  // マスタデータ取得
  useEffect(() => {
    const fetchMasterData = async () => {
      try {
        // 発注先取得
        const vendorsResponse = await salesMasterApi.getVendors();
        if (vendorsResponse.data.success) {
          setVendors(vendorsResponse.data.data);
        }

        // ランクコスト取得
        const rankCostResponse = await fetch('/api/rank-cost', {
          headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
        });
        const rankCostData = await rankCostResponse.json();
        if (rankCostData.success) {
          const costs: Record<string, number> = {};
          rankCostData.data.forEach((rc: { rank: string; monthly_cost: number }) => {
            costs[rc.rank] = rc.monthly_cost;
          });
          setRankCosts(costs);
        }
      } catch (err) {
        console.error('マスタデータ取得エラー:', err);
      }
    };
    fetchMasterData();
  }, []);

  // 金額入力用のフォーマット関数（カンマ区切り、小数点なし）
  const formatInputValue = (value: string | number | null | undefined): string => {
    if (value === '' || value === null || value === undefined) return '';
    const num = Number(value);
    if (isNaN(num)) return '';
    return Math.floor(num).toLocaleString('ja-JP');
  };

  // 要員追加
  const addResource = () => {
    onChange([
      ...resources,
      {
        resource_name: '',
        resource_type: 'employee',
        employee_id: '',
        vendor_id: null,
        unit_price: 0,
        cost_rate: 0,
        start_month: `${fiscalYear}-01-01`,
        end_month: `${fiscalYear}-12-01`,
        monthly: [],
      },
    ]);
  };

  // 要員削除
  const removeResource = (index: number) => {
    const updated = [...resources];
    updated.splice(index, 1);
    onChange(updated);
  };

  // 要員種別変更時の処理
  const handleResourceTypeChange = (index: number, resourceType: string) => {
    const updated = [...resources];
    const isTBAType = ['internal_pool', 'outsource_pool', 'undetermined'].includes(resourceType);
    const needsVendor = ['contractor', 'outsource_pool'].includes(resourceType);

    updated[index] = {
      ...updated[index],
      resource_type: resourceType as 'employee' | 'internal_pool' | 'contractor' | 'outsource_pool' | 'undetermined',
      resource_name: isTBAType ? 'TBA' : '',
      employee_id: resourceType === 'employee' ? updated[index].employee_id : '',
      vendor_id: needsVendor ? updated[index].vendor_id : null,
    };
    onChange(updated);
  };

  // 社員選択時の処理（ランクから原価を自動設定）
  const handleEmployeeSelect = (index: number, employee: TechEmployee | null) => {
    const updated = [...resources];
    if (employee) {
      const costRate = employee.rank ? (rankCosts[employee.rank] || 0) : 0;
      updated[index] = {
        ...updated[index],
        employee_id: employee.employee_id,
        resource_name: employee.name,
        cost_rate: costRate,
      };
    } else {
      updated[index] = {
        ...updated[index],
        employee_id: '',
        resource_name: '',
        cost_rate: 0,
      };
    }
    onChange(updated);
  };

  // フィールド変更
  const handleResourceChange = (index: number, field: string, value: string | number | null) => {
    const updated = [...resources];
    (updated[index] as Record<string, unknown>)[field] = value;
    onChange(updated);
  };

  return (
    <Box>
      {!disabled && (
        <Box sx={{ mb: 2 }}>
          <Button variant="outlined" startIcon={<Add />} onClick={addResource}>
            要員追加
          </Button>
        </Box>
      )}

      {showAlert && (
        <Alert severity="info" sx={{ mb: 2 }}>
          種別を先に選択してください。社員の場合は技術部門の社員から選択、BP/外注一式の場合は発注先を選択できます。社員一式・外注一式・未定の場合は「TBA」が自動設定されます。
        </Alert>
      )}

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell width={120}>種別</TableCell>
              <TableCell width={200}>要員名</TableCell>
              <TableCell width={180}>発注先</TableCell>
              <TableCell align="right">売単価</TableCell>
              <TableCell align="right">原価</TableCell>
              <TableCell>開始月</TableCell>
              <TableCell>終了月</TableCell>
              {!disabled && <TableCell align="center">操作</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {resources.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  要員データがありません
                </TableCell>
              </TableRow>
            ) : (
              resources.map((r, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <Select
                      size="small"
                      value={r.resource_type || 'employee'}
                      onChange={(e) => handleResourceTypeChange(index, e.target.value)}
                      disabled={disabled}
                      sx={{ width: 110 }}
                    >
                      <MenuItem value="employee">社員</MenuItem>
                      <MenuItem value="internal_pool">社員一式</MenuItem>
                      <MenuItem value="contractor">BP</MenuItem>
                      <MenuItem value="outsource_pool">外注一式</MenuItem>
                      <MenuItem value="undetermined">未定</MenuItem>
                    </Select>
                  </TableCell>
                  <TableCell>
                    {r.resource_type === 'employee' ? (
                      <AsyncAutocomplete<TechEmployee>
                        label=""
                        searchApi={async (query) => {
                          const results = await employeeApi.searchEmployees(query, { department_type: 'tech' });
                          return results.map(e => ({
                            employee_id: e.employee_id,
                            name: e.name,
                            rank: e.rank,
                          }));
                        }}
                        getOptionLabel={(option) => typeof option === 'string' ? option : option.name}
                        value={r.employee_id ? { employee_id: r.employee_id, name: r.resource_name || '' } : null}
                        onChange={(employee) => handleEmployeeSelect(index, employee as TechEmployee | null)}
                        disabled={disabled}
                        placeholder="社員を検索..."
                        size="small"
                        isOptionEqualToValue={(option, value) => option.employee_id === value.employee_id}
                        sx={{ width: 200 }}
                        loadOnOpen
                      />
                    ) : r.resource_type === 'contractor' ? (
                      <TextField
                        size="small"
                        value={r.resource_name || ''}
                        onChange={(e) => handleResourceChange(index, 'resource_name', e.target.value)}
                        disabled={disabled}
                        placeholder="BP名を入力"
                        sx={{ width: 180 }}
                      />
                    ) : (
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        TBA
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {['contractor', 'outsource_pool'].includes(r.resource_type || '') ? (
                      <Autocomplete
                        size="small"
                        options={vendors}
                        getOptionLabel={(option) => option.vendor_name}
                        value={vendors.find((v) => v.vendor_id === r.vendor_id) || null}
                        onChange={(_, newValue) => handleResourceChange(index, 'vendor_id', newValue?.vendor_id || null)}
                        disabled={disabled}
                        renderInput={(params) => <TextField {...params} placeholder="発注先" />}
                        sx={{ width: 160 }}
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">-</Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <TextField
                      type="text"
                      size="small"
                      value={focusedField === `resource-${index}-unit_price`
                        ? (r.unit_price || '')
                        : formatInputValue(r.unit_price)}
                      onFocus={() => setFocusedField(`resource-${index}-unit_price`)}
                      onBlur={() => setFocusedField(null)}
                      onChange={(e) => {
                        const rawValue = e.target.value.replace(/,/g, '');
                        if (rawValue === '' || /^\d*$/.test(rawValue)) {
                          handleResourceChange(index, 'unit_price', rawValue === '' ? 0 : parseInt(rawValue));
                        }
                      }}
                      disabled={disabled}
                      sx={{ width: 140 }}
                      inputProps={{ style: { textAlign: 'right' } }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <TextField
                      type="text"
                      size="small"
                      value={focusedField === `resource-${index}-cost_rate`
                        ? (r.cost_rate || '')
                        : formatInputValue(r.cost_rate)}
                      onFocus={() => setFocusedField(`resource-${index}-cost_rate`)}
                      onBlur={() => setFocusedField(null)}
                      onChange={(e) => {
                        const rawValue = e.target.value.replace(/,/g, '');
                        if (rawValue === '' || /^\d*$/.test(rawValue)) {
                          handleResourceChange(index, 'cost_rate', rawValue === '' ? 0 : parseInt(rawValue));
                        }
                      }}
                      disabled={disabled}
                      sx={{ width: 140 }}
                      inputProps={{ style: { textAlign: 'right' } }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      type="month"
                      size="small"
                      value={r.start_month?.substring(0, 7) || ''}
                      onChange={(e) => handleResourceChange(index, 'start_month', e.target.value + '-01')}
                      disabled={disabled}
                      sx={{ width: 150 }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      type="month"
                      size="small"
                      value={r.end_month?.substring(0, 7) || ''}
                      onChange={(e) => handleResourceChange(index, 'end_month', e.target.value + '-01')}
                      disabled={disabled}
                      sx={{ width: 150 }}
                    />
                  </TableCell>
                  {!disabled && (
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => removeResource(index)}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ResourceAllocationTable;
